import React, { Component } from "react";
import { connect } from "react-redux";
import { Headphones, Loader } from "lucide-react";
import placeholderImage from "../../../../Assets/svg/PlaceholderImage1.svg";

class CallTranscript extends Component {
  formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true });
  };

  formatDate = (timestamp) => {
    if (!timestamp) return "Today";
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return "Unknown";
    const cleaned = phoneNumber.replace('+1', '').replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
    }
    return phoneNumber;
  };

  formatDuration = (seconds) => {
    if (!seconds) return "0s";
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return minutes > 0 ? `${minutes}m ${secs}s` : `${secs}s`;
  };

  render() {
    const { selectedCall, callTranscript, isLoadingTranscript, transcriptError } = this.props;

    // If no call is selected, show empty state
    if (!selectedCall) {
      return (
        <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
          <div className="relative w-full h-full border border-gray-200 rounded-xl overflow-hidden flex flex-col bg-white">
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center px-6">
                <Headphones className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-inter text-sm">
                  Select a call to view transcript
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const displayNumber = selectedCall.direction === 'inbound'
      ? selectedCall.from_number
      : selectedCall.to_number;
    const displayName = this.formatPhoneNumber(displayNumber);
    const callDate = this.formatDate(selectedCall.started_at);

    return (
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
        <div className="relative w-full h-full border border-gray-200 rounded-xl overflow-hidden flex flex-col bg-white">

          {/* Header */}
          <div className="w-full h-14 bg-white border-b border-blue-100 px-4 flex justify-between items-center rounded-t-xl shrink-0">
            <div className="flex items-center gap-3">
              <img
                src={placeholderImage}
                alt="Contact"
                className="h-9 w-9 rounded-full bg-primary-blue/10"
              />
              <div>
                <div className="font-SFPro font-semibold text-base leading-tight text-gray-900">
                  {displayName}
                </div>
                <div className="font-inter text-xs text-gray-500">
                  {selectedCall.direction === 'inbound' ? 'Incoming call' : 'Outgoing call'} • {this.formatDuration(selectedCall.duration_seconds)}
                </div>
              </div>
            </div>
          </div>

          {/* Timeline label */}
          <div className="flex items-center justify-center gap-2 py-3 border-b border-gray-100 bg-gray-50 shrink-0">
            <Headphones className="w-4 h-4 text-primary-blue" />
            <span className="font-inter font-semibold text-sm text-gray-700">
              Call Summary: Timeline
            </span>
          </div>

          {/* Date badge */}
          <div className="flex justify-center py-2 shrink-0">
            <span className="font-inter text-xs font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
              {callDate}
            </span>
          </div>

          {/* Transcript messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-3">
            {isLoadingTranscript ? (
              <div className="flex items-center justify-center py-10">
                <Loader className="w-6 h-6 text-primary-blue animate-spin" />
              </div>
            ) : transcriptError ? (
              <div className="px-4 py-6 text-center">
                <p className="text-red-500 text-sm">{transcriptError}</p>
              </div>
            ) : !callTranscript || callTranscript.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <p className="text-gray-500 text-sm">No transcript available for this call</p>
                <p className="text-gray-400 text-xs mt-1">
                  Transcripts are generated during active calls
                </p>
              </div>
            ) : (
              callTranscript.map((entry, i) => {
                const isAgent = entry.speaker === 'agent';
                const role = isAgent ? 'Agent (Bot)' : 'Customer';
                const time = this.formatTime(entry.timestamp);

                return (
                  <div
                    key={entry.id || i}
                    className={`flex flex-col rounded-xl px-4 py-3 gap-1.5 border ${
                      isAgent
                        ? "bg-gradient-to-r from-primary-blue to-blue-400 border-transparent self-end max-w-[75%]"
                        : "bg-gray-50 border-gray-200 self-start max-w-[75%]"
                    }`}
                  >
                    <div className="flex justify-between items-center gap-4">
                      <span
                        className={`font-SFPro font-semibold text-xs ${
                          isAgent ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {role}
                      </span>
                      <span
                        className={`font-inter text-[10px] shrink-0 ${
                          isAgent ? "text-white/70" : "text-gray-400"
                        }`}
                      >
                        {time}
                      </span>
                    </div>
                    <p
                      className={`font-inter text-sm leading-relaxed ${
                        isAgent ? "text-white" : "text-gray-600"
                      }`}
                    >
                      {entry.text}
                    </p>
                    {entry.confidence_score && (
                      <span className={`font-inter text-[9px] mt-1 ${
                        isAgent ? "text-white/50" : "text-gray-400"
                      }`}>
                        Confidence: {(entry.confidence_score * 100).toFixed(0)}%
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  selectedCall: state.calls.selectedCall,
  callTranscript: state.calls.callTranscript,
  isLoadingTranscript: state.calls.isLoadingTranscript,
  transcriptError: state.calls.transcriptError,
});

export default connect(mapStateToProps)(CallTranscript);
